// /practice/<slug>: a call that is not one. His own camera, a tile for the
// guest named after the prep, the same controls a call has (the bar with mic,
// camera, screen share, record, Breeze, leave; Share link and the "..." menu at
// the top right), and the same workspace on the right, so he can run through a
// meeting before it happens. His own camera is the small draggable tile a call
// has, not a tile beside the guest (Adam, 2026-10-04: "during a scheduled call
// picture should still show small instead of being side by side with the person
// i'm talking to"). Nothing connects, nothing records (Record only
// shows what it would look like), and the camera and the shared screen never
// leave the page.

import { useEffect, useRef, useState, type JSX } from "react";
import { ApiError } from "../lib/api";
import { openScreenShare, stopStream, supportsScreenShare } from "../lib/media";
import { navigate } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";
import { BREEZE_PANEL_ENABLED } from "../lib/playbooks";
import { getPrepDoc } from "../lib/prepApi";
import { WIDE_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import { CallMenu, ControlsTray } from "../components/ControlsTray";
import { useDesktopControls } from "../hooks/useDesktopControls";
import { NothingHere } from "../components/NothingHere";
import { DraggableSelfView } from "../components/DraggableSelfView";
import { PrepWorkspace, readBreezeColumnShut, readPrepColumnShut, writeBreezeColumnShut, writePrepColumnShut } from "../components/PrepWorkspace";

type Camera = "asking" | "on" | "off";

function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "")).toUpperCase() || "?";
}

export function PracticeScreen({ slug, handle }: { readonly slug: string; readonly handle: string }): JSX.Element {
  const [title, setTitle] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [camera, setCamera] = useState<Camera>("asking");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const screenRef = useRef<HTMLVideoElement>(null);
  const wide = useMediaQuery(WIDE_QUERY);
  // The mic has nothing to carry here, so it only shows the state a call would.
  const [micOn, setMicOn] = useState(true);
  const [cameraWanted, setCameraWanted] = useState(true);
  const [recording, setRecording] = useState(false);
  // Breeze's column, owned here so the bar's Breeze button can open and close it.
  const desktop = useDesktopControls();
  const [breezeShut, setBreezeShut] = useState(() => (wide ? readBreezeColumnShut() : false));
  const [prepShut, setPrepShut] = useState(() => (wide ? readPrepColumnShut() : false));
  const [screen, setScreen] = useState<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  screenStreamRef.current = screen;

  useEffect(() => {
    const controller = new AbortController();
    getPrepDoc(slug, controller.signal)
      .then((doc) => {
        setTitle(doc.title);
      })
      .catch((err: unknown) => {
        if ((err as Error).name === "AbortError") return;
        if (err instanceof ApiError && err.status === 404) setMissing(true);
        else setTitle("");
      });
    return () => controller.abort();
  }, [slug]);

  // His camera, video only. A refusal or no camera leaves the tile saying so;
  // turning it off lets the camera go, like a call does.
  useEffect(() => {
    if (!cameraWanted) {
      setCamera("off");
      setStream(null);
      return;
    }
    setCamera("asking");
    let got: MediaStream | null = null;
    let cancelled = false;
    const md = navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== "function") {
      setCamera("off");
      return;
    }
    md.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        got = s;
        setStream(s);
        setCamera("on");
      })
      .catch(() => {
        if (!cancelled) setCamera("off");
      });
    return () => {
      cancelled = true;
      got?.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
  }, [cameraWanted]);

  // The shared screen on its own tile. The browser's own "stop sharing" ends it too.
  useEffect(() => {
    const el = screenRef.current;
    if (el) el.srcObject = screen;
    if (!screen) return;
    const track = screen.getVideoTracks()[0];
    const ended = (): void => setScreen(null);
    track?.addEventListener("ended", ended);
    return () => track?.removeEventListener("ended", ended);
  }, [screen]);
  useEffect(() => () => stopStream(screenStreamRef.current), []);

  const toggleScreenShare = (): void => {
    if (screen) {
      stopStream(screen);
      setScreen(null);
      return;
    }
    void openScreenShare().then(setScreen, () => {
      // The picker was dismissed. Nothing to do.
    });
  };

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 2200);
    return () => window.clearTimeout(t);
  }, [notice]);

  const changeBreezeShut = (shut: boolean): void => {
    setBreezeShut(shut);
    if (wide) writeBreezeColumnShut(shut);
  };
  const toggleBreeze = (): void => changeBreezeShut(!breezeShut);
  const changePrepShut = (shut: boolean): void => {
    setPrepShut(shut);
    if (wide) writePrepColumnShut(shut);
  };

  if (missing) return <NothingHere />;

  const guest = title && title.trim() ? title : "Guest";
  return (
    <div className="prac">
      <div className="prac__stage">
        <header className="prac__head">
          <span className="prac__label">Practice</span>
          {recording ? <span className="prac__rec">Recording</span> : null}
        </header>
        <div className="prac__tiles">
          {screen ? (
            <div className="prac__tile prac__tile--screen" aria-label="Your screen">
              <video ref={screenRef} className="prac__screen" autoPlay muted playsInline />
              <span className="prac__name">Your screen</span>
            </div>
          ) : null}
          <div className="prac__tile prac__tile--guest" aria-label={`${guest}`}>
            <span className="prac__avatar" aria-hidden="true">
              {initials(guest)}
            </span>
            <span className="prac__name">{guest}</span>
          </div>
        </div>
        <DraggableSelfView stream={stream} cameraOn={camera === "on"} mirrored flipPhase="idle" />
        <div className="prac__tray">
          <ControlsTray
            micOn={micOn}
            cameraOn={camera === "on"}
            onToggleMic={() => setMicOn((v) => !v)}
            onToggleCamera={() => setCameraWanted((v) => !v)}
            onLeave={() => navigate(ownRoomPath())}
            canShareScreen={supportsScreenShare()}
            sharingScreen={screen !== null}
            onToggleScreenShare={toggleScreenShare}
            recording={recording}
            onToggleRecording={() => setRecording((v) => !v)}
            onToggleBreeze={BREEZE_PANEL_ENABLED ? toggleBreeze : undefined}
            breezeOpen={!breezeShut}
          />
        </div>
        <CallMenu
          canFlipCamera={false}
          canShareScreen={false}
          sharingScreen={false}
          originalSoundOn={false}
          onToggleBreeze={desktop || !BREEZE_PANEL_ENABLED ? undefined : toggleBreeze}
          breezeOpen={!breezeShut}
          prepOpen={desktop && wide ? !prepShut : undefined}
          onOpenPrep={desktop && wide ? () => changePrepShut(!prepShut) : undefined}
        />
        {notice ? (
          <div className="prac__notice" role="status">
            {notice}
          </div>
        ) : null}
      </div>
      <aside className="prac__dock" aria-label="Agenda workspace">
        <PrepWorkspace
          slug={slug}
          columns={wide}
          breezeShut={breezeShut}
          onBreezeShutChange={changeBreezeShut}
          prepShut={desktop && wide ? prepShut : undefined}
          onPrepShutChange={changePrepShut}
          host
        />
      </aside>
    </div>
  );
}
